import React, { useState } from 'react';

import { useBooleanFlag } from 'utils/flags';
import { LaunchDarklyFlags } from 'lib/types/launchDarklyFlags';
import { CustomNoticeFilingType } from 'lib/types/filingType';
import { DEFAULT_MAILING as MAIL_ADDRESS_WITHOUT_STATE } from 'routes/placeScroll/NoticeDelivery/deliveryRecipients.helpers';
import { State } from 'lib/enums';
import { CardGridLayout, GridInput } from 'lib/components/Card/Grid';
import { SwitchControlledCard } from 'lib/components/Card/SwitchControlledCard';
import { SwitchControlledCards } from 'lib/components/Card/SwitchControlledCards';
import LabeledSwitch from 'lib/components/LabeledSwitch';
import { AffidavitMailingCard } from './AffidavitMailingCard';
import { AffidavitTemplateSelect } from './AffidavitTemplateSelect';

export const DEFAULT_AFFIDAVIT_MAILING_ADDRESS = {
  ...MAIL_ADDRESS_WITHOUT_STATE,
  address: {
    ...MAIL_ADDRESS_WITHOUT_STATE.address,
    address_state: State.alabama.value
  }
};

type AffidavitSettingsCardProps = {
  onUpdateNoticeType: (newNoticeType: CustomNoticeFilingType) => void;
  updatedNoticeType: CustomNoticeFilingType;
};

export default function AffidavitSettingsCard({
  onUpdateNoticeType,
  updatedNoticeType
}: AffidavitSettingsCardProps) {
  const [useCustomAffidavitTemplate, setUseCustomAffidavitTemplate] = useState(
    !!updatedNoticeType.defaultAffidavitTemplate
  );
  const [useColumnMailAffidavit, setUseColumnMailAffidavit] = useState(
    !!updatedNoticeType.defaultMailingAddresses?.length
  );

  const affidavitMailingAddresses =
    updatedNoticeType.defaultMailingAddresses || [];

  const enableDeliveryAddOns = useBooleanFlag(
    LaunchDarklyFlags.ENABLE_AFFIDAVIT_INVOICE_ADDONS,
    false
  );
  const mailingAddressDescription = enableDeliveryAddOns
    ? 'If enabled, a copy of the affidavit is mailed to the address you provide. Filers see the address but cannot edit or remove it, and are not charged for the copy.'
    : 'If enabled, the provided mailing address will populate by default in the affidavits section of the placement flow.';

  // When affidavits are not required for this notice type, the dependent
  // settings below are irrelevant: Column won't produce an affidavit, so there
  // is nothing to template or mail.
  const affidavitDisabled = updatedNoticeType.affidavitDisabled === true;

  return (
    <CardGridLayout
      header={{
        title: 'Affidavit Settings',
        description:
          'Configure special affidavit settings for this notice type.'
      }}
    >
      <GridInput fullWidth>
        <LabeledSwitch
          label="Are affidavits required for this notice type?"
          description="If disabled, notices of this type won't require affidavits after publication."
          onChange={newValue => {
            const newNoticeType = { ...updatedNoticeType };
            if (newValue) {
              delete newNoticeType.affidavitDisabled;
            } else {
              newNoticeType.affidavitDisabled = true;
            }
            onUpdateNoticeType(newNoticeType);
          }}
          value={updatedNoticeType.affidavitDisabled !== true}
        />
      </GridInput>
      <SwitchControlledCard
        disabled={affidavitDisabled}
        labelProps={{
          label: 'Use a custom affidavit for this notice type?',
          description:
            'If enabled, this notice type will use a different affidavit template from your default affidavit.',
          value: useCustomAffidavitTemplate,
          onChange: useCustomAffidavit => {
            setUseCustomAffidavitTemplate(useCustomAffidavit);
            if (!useCustomAffidavit) {
              const newNoticeType = { ...updatedNoticeType };
              delete newNoticeType.customAffidavit;
              delete newNoticeType.defaultAffidavitTemplate;
              onUpdateNoticeType(newNoticeType);
            }
          }
        }}
        header=""
      >
        <AffidavitTemplateSelect
          labelText="Custom Template"
          value={updatedNoticeType.defaultAffidavitTemplate}
          onChange={templateRef => {
            onUpdateNoticeType({
              ...updatedNoticeType,
              defaultAffidavitTemplate: templateRef
            });
          }}
          disabled={affidavitDisabled}
        />
      </SwitchControlledCard>
      <SwitchControlledCards
        labelProps={{
          label: 'Should affidavits be mailed for this notice type?',
          description: mailingAddressDescription,
          onChange: newValue => {
            const newNoticeType = { ...updatedNoticeType };
            if (!newValue) {
              delete newNoticeType.defaultMailingAddresses;
            } else {
              newNoticeType.defaultMailingAddresses = [
                DEFAULT_AFFIDAVIT_MAILING_ADDRESS
              ];
            }
            onUpdateNoticeType(newNoticeType);
            setUseColumnMailAffidavit(newValue);
          },
          value: useColumnMailAffidavit,
          disabled: affidavitDisabled
        }}
        cardProps={{
          baseHeader: 'Mailing Address',
          values: affidavitMailingAddresses,
          onChange: defaultMailingAddresses =>
            onUpdateNoticeType({
              ...updatedNoticeType,
              defaultMailingAddresses
            }),
          Component: AffidavitMailingCard,
          createable: {
            buttonText: 'Add Mailing Address',
            initialValue: DEFAULT_AFFIDAVIT_MAILING_ADDRESS
          },
          archivable: {
            buttonText: 'Remove Mailing Address',
            shouldAllow: () => affidavitMailingAddresses.length > 1
          }
        }}
      />
    </CardGridLayout>
  );
}
